import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import BzmIcon from 'components/icons/BzmIcon';
import BlazePagination from 'components/ui/pagination/BlazePagination/BlazePagination';
import { formatDateAndTime } from 'utils/dateUtil';
import { AuditLogRow, AuditLogSort, PAGE_SIZE_OPTIONS } from './types';

interface AuditLogListProps {
  rows: AuditLogRow[];
  isLoading: boolean;
  page: number;
  pageSize: number;
  total: number;
  sort: AuditLogSort;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSort: (sort: AuditLogSort) => void;
}

const AuditLogList: React.FC<AuditLogListProps> = ({
  rows,
  isLoading,
  page,
  pageSize,
  total,
  sort,
  onPageChange,
  onPageSizeChange,
  onSort,
}) => {
  const { t } = useTranslation('admin');

  // The log is always ordered by time; the only sort control is the direction toggle.
  const newestFirst = sort.direction === 'DESC';
  const toggleSort = () => onSort({ field: 'occurredAt', direction: newestFirst ? 'ASC' : 'DESC' });

  const isEmpty = rows.length === 0 && !isLoading;

  return (
    <div className="audit-log">
      <div className="audit-log__header" role="row">
        <button
          type="button"
          className="audit-log__header-sort"
          onClick={toggleSort}
          title={newestFirst ? t('auditLog.sortNewestFirst') : t('auditLog.sortOldestFirst')}
        >
          <span role="columnheader">{t('auditLog.columns.time')}</span>
          <BzmIcon
            size={13}
            className="audit-log__header-sort-arrow"
            variant={newestFirst ? 'icon-bzm-arrow-down' : 'icon-bzm-arrow-up'}
          />
        </button>
        <span className="audit-log__header-static" role="columnheader">
          {t('auditLog.columns.event')}
        </span>
        <span className="audit-log__header-static" role="columnheader">
          {t('auditLog.columns.message')}
        </span>
        <span className="audit-log__header-static" role="columnheader">
          {t('auditLog.columns.workspaceUser')}
        </span>
      </div>

      {isEmpty ? (
        <div className="audit-log__empty">{t('auditLog.empty')}</div>
      ) : (
        <ul className={clsx('audit-log__list', { 'audit-log__list--loading': isLoading })} aria-busy={isLoading}>
          {rows.map(row => (
            <li key={row.id} className={`audit-log__entry audit-log__entry--${row.eventType.toLowerCase()}`}>
              <time className="audit-log__time" dateTime={row.occurredAt}>
                {formatDateAndTime(new Date(row.occurredAt), true)}
              </time>
              <span className="audit-log__event">
                <span className="audit-log__dot" aria-hidden="true" />
                {t(`auditLog.eventTypes.${row.eventType}`, { defaultValue: row.eventType })}
              </span>
              <span className="audit-log__message">
                {row.message}
              </span>
              <span className="audit-log__meta">
                {row.workspaceName} · {row.userEmail || '—'}
              </span>
            </li>
          ))}
        </ul>
      )}

      {total > 0 && (
        <BlazePagination
          pageSize={pageSize}
          total={total}
          activePage={page + 1}
          pageSelectedHandler={(oneBasedPage: number) => onPageChange(oneBasedPage - 1)}
          pageSizeSelectedHandler={onPageSizeChange}
          pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
          showPageSizeDropdown={true}
        />
      )}
    </div>
  );
};

export default AuditLogList;
